// The client's review of one month, as one read model: which posts wait on
// them, what each row and banner says, the Approved tag once they answered,
// and which comments they may read (and when comments close). Pure and React-free, so the
// page, the phone and scripts/test-client-review.mjs all read the same answer.
//
// DESIGN.md "The status banner": the client's meta starts "Idea and brief ·"
// and never says Production. "Comments sit in parts": the client reads shared
// comments only, never a private note from before and never M7's drafts.
// There is no version comparison: the version menu lists the sent versions.
//
// The answer-by date and the team's note come per post with the send (the
// slot's for the idea, the brief's for the brief) and are null once answered.
//
// ONE ANSWER PER POST (14 Oct). The idea and its creative brief go out
// together and the client answers once: the Concept and Creative brief tabs
// both carry the same banner and the same Approve / Ask for a change, which
// decide the post (the idea decision, which the backend applies to the brief
// in the same write). The brief answers on its own only in the old loop: a
// post whose idea was approved before the two were merged (`briefAlone`).

import { MEDIA_TYPE_LABEL } from "@/types/contentCalendar.type";
import type { PortalComment, PortalConceptVersion, PortalSlot } from "@/types/clientPortal.type";
import type { ClientBrief } from "./api/useClientBriefs";
import type { BannerIcon, BannerPrimary, PostBanner } from "../dashboard/content-calendar/postState";
import { shortDate } from "../dashboard/content-calendar/postState";
import {
  briefThreads,
  commentsClosedLine,
  conceptThreads,
  laterOf,
  type CommentThreadData,
} from "../dashboard/content-calendar/comments/commentModel";

/** Who made the posts. The portal has no field for it; the product says Mohtawa. */
export const AGENCY = "Mohtawa";

export type ClientRound = "concept" | "brief";
/** What one answer decides: the idea alone (an old send), the brief alone (the old loop), or the post. */
export type ClientAnswer = ClientRound | "post";
export type ClientGroup = "review" | "upcoming" | "approved";
export type ClientTurnTone = "you" | "late" | "none";

/** One post in the client's list: the slot and, once sent, its brief. */
export interface ClientPost {
  slot: PortalSlot;
  /** Only a brief that was sent to the client is ever here. */
  brief: ClientBrief | null;
}

/** Fields the backend does not send yet. Absent means hidden. */
export interface ClientFuture {
  /** #129: the idea and the brief went out together as one review. */
  joint?: boolean;
  /** When the post goes live, when the portal knows it. */
  publishAt?: string | null;
}

// ------------------------------------------------------------ versions

/** The concept version the client may answer, else the newest one. */
export function activeConceptVersion(slot: PortalSlot): PortalConceptVersion | null {
  const versions = slot.conceptVersions ?? [];
  if (!versions.length) return null;
  return (
    versions.find((version) => version.id === slot.activeConceptVersionId) ??
    [...versions].sort((a, b) => b.version - a.version)[0] ??
    null
  );
}

/** Concept versions, oldest first. */
export function conceptVersionsAsc(slot: PortalSlot): PortalConceptVersion[] {
  return [...(slot.conceptVersions ?? [])].sort((a, b) => a.version - b.version);
}

/** The version menu: only the versions that were sent, each with its date, newest first. */
export function sentConceptVersions(slot: PortalSlot): { version: number; createdAt: string }[] {
  return conceptVersionsAsc(slot)
    .filter((entry) => !!entry.sharedAt)
    .reverse()
    .map((entry) => ({ version: entry.version, createdAt: entry.sharedAt as string }));
}

// ------------------------------------------------------------ the rounds

export function conceptApproved(slot: PortalSlot): boolean {
  // The team may approve for the client: no decision is written, and its
  // source is the only sign. It wins over an older decline still on the slot.
  if (slot.approvalSource) return true;
  if (slot.decision) return slot.decision.decision === "APPROVED";
  return activeConceptVersion(slot)?.decision?.decision === "APPROVED";
}

export function conceptAwaiting(slot: PortalSlot): boolean {
  return slot.phase === "awaiting_you" && slot.actionable;
}

export function briefAwaiting(brief: ClientBrief | null): boolean {
  return !!brief && brief.phase === "awaiting_you" && brief.actionable;
}

export function briefApproved(brief: ClientBrief | null): boolean {
  if (brief?.acceptanceSource) return true;
  return brief?.decision?.decision === "APPROVED";
}

/** The brief tab exists once a brief was sent: the idea and its brief go out together (13 Oct). */
export function briefVisible(post: ClientPost): boolean {
  return !!post.brief;
}

/**
 * The brief still has its own answer: the idea was approved before the idea
 * and the brief became one answer, and the brief is not approved yet.
 */
export function briefAlone(post: ClientPost): boolean {
  return !!post.brief && conceptApproved(post.slot) && !briefApproved(post.brief);
}

/** What an answer given on this tab decides. */
export function answerOf(post: ClientPost, tab: ClientRound): ClientAnswer {
  if (tab === "brief" && briefAlone(post)) return "brief";
  return post.brief ? "post" : "concept";
}

/** The round the post is in for the client: the idea with its brief, or the brief alone in the old loop. */
export function roundOf(post: ClientPost): ClientRound {
  return briefAlone(post) ? "brief" : "concept";
}

export function groupOf(post: ClientPost): ClientGroup {
  if (conceptAwaiting(post.slot) || (briefVisible(post) && briefAwaiting(post.brief))) return "review";
  if (conceptApproved(post.slot) && briefApproved(post.brief)) return "approved";
  return "upcoming";
}

export interface ClientGroups {
  review: ClientPost[];
  upcoming: ClientPost[];
  approved: ClientPost[];
}

/** For your review, Coming up and Approved, each in date order. */
export function groupPosts(posts: readonly ClientPost[]): ClientGroups {
  const ordered = [...posts].sort(
    (a, b) => a.slot.scheduledAt.localeCompare(b.slot.scheduledAt) || a.slot.id.localeCompare(b.slot.id),
  );
  const groups: ClientGroups = { review: [], upcoming: [], approved: [] };
  for (const post of ordered) groups[groupOf(post)].push(post);
  return groups;
}

/** The list's order, which J and K walk. */
export function listOrder(posts: readonly ClientPost[]): ClientPost[] {
  const groups = groupPosts(posts);
  return [...groups.review, ...groups.upcoming, ...groups.approved];
}

// ------------------------------------------------------------ the list

/** "Video 9:16". */
export function formatShort(slot: Pick<PortalSlot, "mediaType" | "aspectRatio">): string {
  return `${MEDIA_TYPE_LABEL[slot.mediaType]} ${slot.aspectRatio}`;
}

/** The day the open round asks for an answer by, while it waits on the client. */
export function answerByOf(post: ClientPost, round: ClientRound = roundOf(post)): string | null {
  if (round === "brief") return briefAwaiting(post.brief) ? (post.brief?.answerBy ?? null) : null;
  return conceptAwaiting(post.slot) ? (post.slot.answerBy ?? null) : null;
}

/** The team's note on the open round's send, while it waits on the client. */
export function sendNoteOf(post: ClientPost, round: ClientRound = roundOf(post)): string | null {
  const note = round === "brief" ? (briefAwaiting(post.brief) ? post.brief?.note : null) : conceptAwaiting(post.slot) ? post.slot.note : null;
  return note?.trim() || null;
}

/** The version the row and the banner speak of. */
export function roundVersion(post: ClientPost): number {
  if (roundOf(post) === "brief" && post.brief) return post.brief.version;
  return activeConceptVersion(post.slot)?.version ?? 1;
}

/** "Video 9:16 · concept · version 1"; with #129, "concept and brief". */
export function rowMeta(post: ClientPost, future: ClientFuture = {}): string {
  const round = roundOf(post) === "brief" ? "creative brief" : post.brief || future.joint ? "idea and brief" : "concept";
  // Once the reader approved it, the row says when instead of the round:
  // "Video 9:16 · version 2 · you approved Mon 23 Nov".
  if (groupOf(post) !== "review") {
    const decision = roundOf(post) === "brief" ? post.brief?.decision : post.slot.decision;
    // A brief's decision does not say whose it was: it reads "approved".
    const who = roundOf(post) === "brief" ? "approved" : post.slot.decision?.mine ? "you approved" : "approved";
    const on = decision?.decision === "APPROVED" ? shortDate(decision.decidedAt) : null;
    if (on) return `${formatShort(post.slot)} · version ${roundVersion(post)} · ${who} ${on}`;
  }
  return `${formatShort(post.slot)} · ${round} · version ${roundVersion(post)}`;
}

/** Short day and time, "Wed 2 Dec, 18:00". UTC, like every calendar day. */
function dayAndTime(iso: string): string | null {
  const day = shortDate(iso);
  if (!day) return null;
  const date = new Date(iso);
  const time = `${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")}`;
  return `${day}, ${time}`;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whose turn it is, in the row's words. */
export function turnLine(
  post: ClientPost,
  now: number,
  future: ClientFuture = {},
): { tone: ClientTurnTone; label: string } {
  const group = groupOf(post);
  if (group === "review") {
    const answerBy = answerByOf(post);
    // Late from the day after the answer-by day, counted from that day.
    const by = answerBy ? Date.parse(answerBy) : NaN;
    if (!Number.isNaN(by) && now >= by + DAY_MS) {
      const days = Math.max(1, Math.floor((now - by) / DAY_MS));
      return { tone: "late", label: `Your review, ${days} ${days === 1 ? "day" : "days"} late` };
    }
    return { tone: "you", label: "Your review" };
  }
  const live = future.publishAt ? dayAndTime(future.publishAt) : null;
  if (live) return { tone: "none", label: `Goes live ${live}` };
  const { slot, brief } = post;
  if (group === "approved") {
    const decision = brief?.decision ?? null;
    const mine = slot.decision?.mine ?? true;
    return {
      tone: "none",
      label: mine || !decision ? "You approved the idea and brief" : `Approved by ${decision.clientName}`,
    };
  }
  if (slot.phase === "missed") return { tone: "none", label: "The date passed" };
  if (brief?.decision?.decision === "DECLINED" || slot.decision?.decision === "DECLINED" || slot.phase === "in_progress") {
    return { tone: "none", label: `${AGENCY} is working on your changes` };
  }
  if (conceptApproved(slot) && !brief) return { tone: "none", label: `${AGENCY} is writing the creative brief` };
  return { tone: "none", label: `With ${AGENCY}` };
}

/** One sentence under the month: how many posts wait on the client. */
export function homeSentence(
  posts: readonly ClientPost[],
  now: number,
  future: ClientFuture = {},
  /** A guest who reads the month but cannot answer: nothing waits on them. */
  readOnly = false,
): string {
  const waiting = posts.filter((post) => groupOf(post) === "review");
  const count = waiting.length;
  if (readOnly) {
    if (count === 0) return "Nothing is in review right now.";
    return count === 1 ? "One post is in review." : `${count} posts are in review.`;
  }
  // The earliest answer-by day among the posts that wait.
  const dates = waiting.map((post) => answerByOf(post)).filter((iso): iso is string => !!iso).sort();
  const by = dates[0] ? shortDate(dates[0]) : null;
  if (by && count > 0) {
    const late = waiting.filter((post) => turnLine(post, now, future).tone === "late").length;
    const lateLine = late === 0 ? "" : late === 1 ? " One post is late." : ` ${late} posts are late.`;
    return `Please answer by ${by}.${lateLine}`;
  }
  if (count === 0) return "Nothing is waiting for you.";
  if (count === 1) return "One post is waiting for you.";
  return `${count} posts are waiting for you.`;
}

// ------------------------------------------------------------ the banner

export interface ClientBanner extends PostBanner {
  /** The page's one primary: Approve, one word, for the open round. */
  primary?: BannerPrimary;
  /** Request changes sits beside it as a visible secondary. */
  canAskForChanges: boolean;
}

/** Every call to action on the client's page is one or two words. */
export const APPROVE_LABEL = "Approve";
export const REQUEST_CHANGES_LABEL = "Ask for a change";

function banner(icon: BannerIcon, title: string, meta: string, extra: Partial<ClientBanner> = {}): ClientBanner {
  return { icon, title, meta: `Idea and brief · ${meta}`, canAskForChanges: false, ...extra };
}

/** "Thu 3 Sep, 11:05" in the reader's time. */
function dayAndClock(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const day = date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }).replace(",", "").replace("Sept", "Sep");
  return `${day}, ${date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false })}`;
}

/** Whole days from now to a day (negative once it passed). */
function daysUntil(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

function decidedOn(iso: string | null | undefined): string {
  const day = shortDate(iso);
  return day ? ` on ${day}` : "";
}

/**
 * The client's banner for the open tab. `viewingVersion` is the version on
 * screen; an older one is read only and says which is current.
 */
export function clientBanner(
  post: ClientPost,
  tab: ClientRound,
  options: {
    viewingVersion?: number | null;
    sentAt?: string | null;
    /** The reader, to tell their own answer from a colleague's on a brief. */
    readerName?: string | null;
    future?: ClientFuture;
  } = {},
): ClientBanner {
  const { slot, brief } = post;
  // The brief tab carries the post's one answer, except in the old loop where
  // the brief is answered alone (`briefAlone`).
  const alone = tab === "brief" && briefAlone(post);
  const answerBy = answerByOf(post, alone ? "brief" : "concept");
  const by = answerBy ? shortDate(answerBy) : null;
  const yourReview = by ? `Your review, by ${by}` : "Your review";
  const hear = `${AGENCY} sees each comment the moment you post it`;

  if (tab === "brief" && brief && options.viewingVersion && options.viewingVersion !== brief.version) {
    return banner("Clock", `Version ${options.viewingVersion}, read only`, `Version ${brief.version} is the current creative brief`);
  }
  if (alone && brief) {
    const decision = brief.decision;
    if (briefAwaiting(brief)) {
      const sent = shortDate(options.sentAt);
      return banner(
        "MessageSquare",
        yourReview,
        `Creative brief version ${brief.version}${sent ? `, sent ${sent}` : ""}. Approve it, or comment on any part: ${hear}.`,
        { primary: { label: APPROVE_LABEL, action: "approve-brief" }, canAskForChanges: true },
      );
    }
    if (brief.acceptanceSource === "TEAM") {
      return banner("CheckCheck", "The creative brief is approved", `${AGENCY} takes it from here`);
    }
    const who = !decision || !options.readerName || decision.clientName === options.readerName ? "You" : decision.clientName;
    if (decision?.decision === "APPROVED") {
      return banner("CheckCheck", `${who} approved the creative brief${decidedOn(decision.decidedAt)}`, `${AGENCY} takes it from here`);
    }
    if (decision?.decision === "DECLINED") {
      return banner("RotateCcw", `${who} asked for changes${decidedOn(decision.decidedAt)}`, `${AGENCY} is working on the creative brief`);
    }
    return banner("Clock", `With ${AGENCY}`, `${AGENCY} is revising the creative brief`);
  }

  const active = activeConceptVersion(slot);
  const current = active?.version ?? 1;
  if (tab === "concept" && options.viewingVersion && options.viewingVersion !== current) {
    return banner("Clock", `Version ${options.viewingVersion}, read only`, `Version ${current} is the current idea`);
  }
  if (conceptAwaiting(slot)) {
    const sentIso = options.sentAt ?? active?.sharedAt ?? null;
    const sent = shortDate(sentIso);
    const primary = { primary: { label: APPROVE_LABEL, action: "approve-concept" as const }, canAskForChanges: true };
    // Figma 08: "Waiting for you, by Thu 3 Sep" (· N days late once it passed).
    const left = answerBy ? daysUntil(answerBy) : null;
    const title = by ? `Waiting for you, by ${by}${left !== null && left < 0 ? ` · ${-left} ${left === -1 ? "day" : "days"} late` : ""}` : "Waiting for you";
    const icon: BannerIcon = current > 1 ? "RotateCcw" : "MessageSquare";
    // A send from before the merge carries the idea alone.
    const what = brief ? "The idea and its brief" : "The idea";
    const once = brief ? " One answer covers both." : "";
    // A later version answers what they asked for (Pen MHs3c).
    if (current > 1) {
      const when = dayAndClock(sentIso);
      return { icon, title, meta: `Version ${current}${when ? `, sent ${when}` : ""}. It answers what you asked for.${once} Approve, or ask for a change on any part.`, ...primary };
    }
    const meta =
      left !== null && left >= 0
        ? `${left === 0 ? "Due today" : `${left} ${left === 1 ? "day" : "days"} left`}. ${what}, sent ${sent ?? "to you"}.${once}`
        : `${what}, sent ${sent ?? "to you"}.${once} Approve, or ask for a change on any part.`;
    return { icon, title, meta, ...primary };
  }
  const afterIdea = briefAwaiting(brief)
    ? "The creative brief is waiting for you on its tab"
    : briefApproved(brief)
      ? "The creative brief is approved too"
      : `${AGENCY} is writing the creative brief`;
  // Approved by the team on their behalf: any decision on the slot is older.
  if (slot.approvalSource === "TEAM") {
    return briefApproved(brief)
      ? banner("CheckCheck", "The idea and its brief are approved", `${AGENCY} takes it from here`)
      : banner("CheckCheck", "The idea is approved", afterIdea);
  }
  const decision = slot.decision ?? (active?.decision ? { ...active.decision, mine: false, carriedOver: false } : null);
  if (decision?.decision === "APPROVED") {
    const who = decision.mine ? "you" : (decision.clientName || "your team").split(" ")[0];
    const when = dayAndClock(decision.decidedAt);
    return { icon: "CheckCheck", title: "Approved", meta: `Approved${when ? ` ${when}` : ""} by ${who}. The team adds the files and schedules it.`, canAskForChanges: false };
  }
  if (decision?.decision === "DECLINED") {
    const who = decision.mine ? "You" : (decision.clientName || "Your team").split(" ")[0];
    const when = dayAndClock(decision.decidedAt);
    return {
      icon: "Clock",
      title: "With the team",
      meta: `${who} asked for a change${when ? ` ${when}` : ""}. The team is on it; you will get version ${current + 1} by email.`,
      canAskForChanges: false,
    };
  }
  if (slot.phase === "missed") return banner("CalendarX", "The date passed", "This post was not answered before its date");
  if (slot.phase === "in_progress") return banner("Clock", `With ${AGENCY}`, `${AGENCY} is preparing a new version`);
  return banner("Clock", "Review closed", "This version is read only");
}

// ------------------------------------------------------------ approved

/** "Mon 23 Nov, 20:20" in the reader's time (or the zone given, for tests). */
function dateAndClock(iso: string, timeZone?: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const weekday = date.toLocaleDateString("en-GB", { weekday: "short", timeZone });
  const day = date.toLocaleDateString("en-GB", { day: "numeric", timeZone });
  const month = date.toLocaleDateString("en-GB", { month: "short", timeZone });
  const clock = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone });
  return `${weekday} ${day} ${month.replace("Sept", "Sep")}, ${clock}`;
}

export interface ClientApproval {
  /** The header tag's words, "Approved". */
  label: string;
  /** "Approved by Noura Alami, Brand · Mon 23 Nov, 20:20 · version 2". */
  tooltip: string;
}

/**
 * Once the open round is approved the banner goes and the header carries an
 * Approved tag; its tooltip says who, for which client, when, and which
 * version. Null while the round is not approved.
 */
export function clientApproval(
  post: ClientPost,
  tab: ClientRound,
  brandName: string,
  timeZone?: string,
): ClientApproval | null {
  const { slot, brief } = post;
  let decision: { clientName: string; decidedAt: string | null } | null = null;
  let version: number | null = null;
  if (tab === "brief") {
    if (!brief || !briefApproved(brief)) return null;
    decision = brief.decision;
    version = brief.version;
  } else {
    if (!conceptApproved(slot)) return null;
    const active = activeConceptVersion(slot);
    decision = slot.decision ?? active?.decision ?? null;
    version = active?.version ?? null;
  }
  // A team approval has no decision of the client's: any on the slot is older.
  // It names only whoever the team recorded as approving outside Creative OS
  // (#129). Otherwise name the approver only when the decision is plainly the
  // client's (a decision with a client name, on a round the client decided).
  const team = (tab === "brief" ? brief?.acceptanceSource : slot.approvalSource) === "TEAM";
  const outside = team ? (tab === "brief" ? brief?.approvedOutside : slot.approvedOutside) : null;
  const phase = tab === "brief" ? brief?.phase : slot.phase;
  const who = team ? outside?.approvedByName?.trim() : phase === "decided" ? decision?.clientName?.trim() : undefined;
  const brand = brandName.trim();
  const by = who ? `Approved by ${who}${brand ? `, ${brand}` : ""}` : "Approved";
  const at = team ? (outside?.approvedAt ?? null) : (decision?.decidedAt ?? null);
  const when = at ? dateAndClock(at, timeZone) : null;
  const parts = [by, when, version !== null ? `version ${version}` : null].filter(Boolean);
  return { label: "Approved", tooltip: parts.join(" · ") };
}

/** The banner goes once the open round is approved, unless an older version is on screen. */
export function showsBanner(post: ClientPost, tab: ClientRound, viewingVersion: number | null): boolean {
  if (viewingVersion !== null) return true;
  return tab === "brief" ? !briefApproved(post.brief) : !conceptApproved(post.slot);
}

/** The toast an approval shows once it is in. */
export function approvedToast(round: ClientAnswer, future: ClientFuture = {}): string {
  if (round === "post" || future.joint) return "You approved the idea and the brief";
  return round === "brief" ? "You approved the creative brief" : "You approved the idea";
}

// ------------------------------------------------------------ comments

type WithVisibility = PortalComment & { visibility?: "SHARED" | "INTERNAL" };

/**
 * Every comment the client may read on this post: shared concept comments on
 * every sent version and the brief's comments. A Team only note never reaches
 * this list, even if an API were to send one.
 */
export function clientThreads(post: ClientPost): CommentThreadData[] {
  const versions = conceptVersionsAsc(post.slot).map((version) => ({
    version: version.version,
    comments: (version.comments as WithVisibility[]).filter((comment) => comment.visibility !== "INTERNAL"),
  }));
  // A legacy post without versions keeps its comments on the idea.
  const legacy =
    versions.length === 0 && post.slot.idea
      ? [{ version: 1, comments: (post.slot.idea.comments as WithVisibility[]).filter((c) => c.visibility !== "INTERNAL") }]
      : [];
  const concept = conceptThreads([...versions, ...legacy], "client");
  const brief = post.brief && briefVisible(post) ? briefThreads(post.brief.comments) : [];
  return [...concept, ...brief].map((thread) => ({
    ...thread,
    anchor: thread.anchor ? { ...thread.anchor, label: partLabel(thread.anchor.label) } : thread.anchor,
    m7Change: undefined,
  }));
}

/** How a part reads after "Concept ·": "the hook", "shot 03" (the API writes "The hook", "Shot 3"). */
export function partLabel(label: string): string {
  const row = label.match(/^(Shot|Slide) (\d+)$/);
  if (row) return `${row[1].toLowerCase()} ${row[2].padStart(2, "0")}`;
  return label.replace(/^The /, "the ");
}

/**
 * No feedback after approval: once the idea and the brief are both approved
 * the composer is one muted line, dated by the later approval.
 */
export function clientCommentsClosed(post: ClientPost, brandName: string): string | null {
  if (!conceptApproved(post.slot) || !briefApproved(post.brief)) return null;
  const { slot, brief } = post;
  const active = activeConceptVersion(slot);
  // A team approval is dated by the outside approval it recorded, if any: a
  // decision still on the post is older than the approval.
  const conceptAt =
    slot.approvalSource === "TEAM"
      ? slot.approvedOutside?.approvedAt
      : (slot.decision?.decidedAt ?? active?.decision?.decidedAt);
  const briefAt = brief?.acceptanceSource === "TEAM" ? brief.approvedOutside?.approvedAt : brief?.decision?.decidedAt;
  // The line names the client only when both approvals were theirs, never for one the team gave.
  const byTeam = slot.approvalSource === "TEAM" || brief?.acceptanceSource === "TEAM";
  return commentsClosedLine(brandName, shortDate(laterOf(conceptAt, briefAt)), byTeam ? "team" : "client");
}

/** "Version 1 · Mohtawa sees each comment and reply the moment you post it." */
export function railSubtitle(version: number): string {
  return `Version ${version} · ${AGENCY} sees each comment and reply the moment you post it.`;
}

// ------------------------------------------------------------ brief versions

export type ClientBriefVersionEntry = NonNullable<ClientBrief["versions"]>[number];

/**
 * Every brief version the client can read, oldest first: the API's list when
 * it sends one, else the previous version and the current one.
 */
export function briefVersionEntries(brief: ClientBrief): ClientBriefVersionEntry[] {
  const entries = new Map<number, ClientBriefVersionEntry>();
  const usable = (entry: ClientBriefVersionEntry) =>
    Number.isInteger(entry.version) && entry.version > 0 && entry.version <= brief.version;
  if (brief.previousVersion && usable(brief.previousVersion)) entries.set(brief.previousVersion.version, brief.previousVersion);
  for (const entry of brief.versions ?? []) if (usable(entry)) entries.set(entry.version, entry);
  entries.set(brief.version, { version: brief.version, body: brief.body, reason: entries.get(brief.version)?.reason ?? null });
  return [...entries.values()].sort((a, b) => a.version - b.version);
}
